import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PaperPlaneTilt, FileText, CalendarCheck } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { useApi, errText } from "@/lib/api";
import { Recipients } from "@/pages/AlertSettings";
import { BoardPreview } from "@/components/alerts/BoardPreview";

const Toggle = ({ id, checked, onChange, title, desc, icon: Icon }) => (
  <div className="flex items-start gap-4">
    <Icon size={20} className="text-[#00FF9D] mt-0.5 shrink-0" />
    <div className="flex-1">
      <p className="font-medium">{title}</p>
      <p className="text-sm text-[#A3A3A3]">{desc}</p>
    </div>
    <Switch data-testid={id} checked={checked} onCheckedChange={onChange} className="data-[state=checked]:bg-[#00FF9D]" />
  </div>
);

export const ReportSettings = ({ founder, cofounders }) => {
  const api = useApi();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["report-settings"], queryFn: () => api.get("/reports/settings") });
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(null);
  const pick = (d) => ({ board_monthly: d.board_monthly, investors: d.investors, weekly_digest: d.weekly_digest, board_note: d.board_note || "" });
  useEffect(() => { if (data) setF(pick(data)); }, [data]);
  if (!f) return null;
  const dirty = JSON.stringify(f) !== JSON.stringify(pick(data));

  const save = async () => {
    setBusy("save");
    try { qc.setQueryData(["report-settings"], await api.put("/reports/settings", f)); toast.success("Report settings saved"); }
    catch (e) { toast.error(e.response?.status === 422 ? "Check the investor email addresses" : errText(e, "Couldn't save report settings")); }
    setBusy(null);
  };
  const sendNow = async () => {
    setBusy("send");
    try {
      const r = await api.post("/reports/board/send-now");
      toast.success(`Board update sent to ${r.delivered_to.length} ${r.delivered_to.length === 1 ? "person" : "people"}`);
      qc.invalidateQueries({ queryKey: ["email-history"] });
      qc.invalidateQueries({ queryKey: ["board-tracking"] });
      qc.invalidateQueries({ queryKey: ["report-settings"] });
      qc.invalidateQueries({ queryKey: ["usage-history"] });
    } catch (e) { toast.error(errText(e, "Couldn't send the board update")); }
    setBusy(null);
  };

  return (
    <section data-testid="report-settings" className="mt-10 rounded-xl border border-[#2A2A2A] bg-[#121212] p-6 space-y-6">
      <h3 className="font-display text-lg">Scheduled emails</h3>
      <Toggle id="board-monthly-toggle" icon={FileText} checked={f.board_monthly} onChange={(v) => setF({ ...f, board_monthly: v })}
        title="Monthly board update" desc="On the 1st of each month: key numbers, Zero Cash Date and a secure link to the board PDF (expires after 30 days). Goes to you, your co-founders and the investors below." />
      <div className="pl-9">
        <p className="text-sm mb-3 text-[#A3A3A3]">Investors</p>
        <Recipients list={f.investors} setList={(investors) => setF({ ...f, investors })} max={data.max_investors}
          prefix="investor" noun="investors" exclude={[founder?.toLowerCase(), ...cofounders].filter(Boolean)} />
        <p className="text-sm mt-6 mb-2 text-[#A3A3A3]">Personal note <span className="text-[11px]">· shown at the top of every board email until you change it</span></p>
        <textarea data-testid="board-note-input" value={f.board_note} maxLength={data.max_note} rows={4}
          onChange={(e) => setF({ ...f, board_note: e.target.value })}
          placeholder="e.g. Strong month: we closed two enterprise pilots and pushed our Zero Cash Date out by six weeks."
          className="w-full max-w-xl rounded-xl bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-sm focus:ring-2 focus:ring-[#00FF9D] focus:outline-none placeholder:text-[#555]" />
        <p data-testid="board-note-count" className="text-[11px] text-[#A3A3A3]">{f.board_note.length}/{data.max_note}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <BoardPreview note={f.board_note} />
          <button data-testid="board-send-now-btn" onClick={sendNow} disabled={busy === "send" || dirty}
            className="flex items-center gap-2 rounded-full border border-[#2A2A2A] px-4 py-2 text-sm hover:border-[#00FF9D] hover:text-[#00FF9D] transition-colors disabled:opacity-40">
            <PaperPlaneTilt size={14} /> {busy === "send" ? "Sending…" : "Send this month's update now"}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-[#A3A3A3]">The preview shows your current note. Sending uses the saved note and recipients, so save changes first.</p>
        {data.caps && (
          <p data-testid="email-caps" className="mt-1 text-[11px] font-mono text-[#A3A3A3]">
            This week: <span data-testid="board-sends-left" className={data.caps.board_sends_left === 0 ? "text-[#FFB800]" : "text-white"}>{data.caps.board_sends_left}/{data.caps.board_sends_per_week}</span> board sends left (the monthly email counts) ·{" "}
            <span data-testid="new-investors-left" className={data.caps.new_investors_left === 0 ? "text-[#FFB800]" : "text-white"}>{data.caps.new_investors_left}/{data.caps.new_investors_per_week}</span> new investor addresses left · resets Monday
          </p>
        )}
      </div>
      <Toggle id="weekly-digest-toggle" icon={CalendarCheck} checked={f.weekly_digest} onChange={(v) => setF({ ...f, weekly_digest: v })}
        title="Weekly runway digest" desc="Every Monday: cash, burn, MRR, how the Zero Cash Date moved, and highlights of the three biggest spending changes since last week. Goes to you and your co-founders." />
      <div className="pl-9"><BoardPreview digest /></div>
      <button data-testid="report-settings-save-btn" onClick={save} disabled={!dirty || busy === "save"}
        className="rounded-full bg-[#00FF9D] px-8 py-3 font-semibold text-[#050505] hover:shadow-[0_0_24px_rgba(0,255,157,0.5)] transition-shadow disabled:opacity-40">
        {busy === "save" ? "Saving…" : "Save report settings"}
      </button>
    </section>
  );
};
